import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { lazy, Suspense } from 'react';
import { useController, type Control } from 'react-hook-form';
import type { SusFormValues } from './susFormSchema';
import type { SusPosition } from '@/components/Sus/map/susPosition';

// Leaflet n'est chargé que lorsque la carte est affichée.
const SusMapPicker = lazy(() => import('@/components/Sus/map/SusMapPicker'));

type Props = {
  control: Control<SusFormValues>;
  disabled: boolean;
};

/** Choix de la latitude et de la longitude en cliquant sur une carte. */
const SusPositionField = ({ control, disabled }: Props) => {
  const { t } = useTranslate();
  const {
    field: latitude,
    fieldState: { error },
  } = useController({ control, name: 'latitude' });
  const { field: longitude } = useController({ control, name: 'longitude' });

  const value: SusPosition | null =
    latitude.value == null || longitude.value == null
      ? null
      : { latitude: latitude.value, longitude: longitude.value };

  const handleChange = (position: SusPosition | null) => {
    latitude.onChange(position?.latitude ?? null);
    longitude.onChange(position?.longitude ?? null);
  };

  return (
    // `isolation` confine les z-index de Leaflet (jusqu'à 1000) pour ne pas recouvrir le pied de page collant.
    <Stack gap="xs" style={{ isolation: 'isolate' }}>
      <Text size="xs" c="dimmed">
        {t('admin.sus.form.position.hint')}
      </Text>
      <Suspense fallback={<Loader size="sm" aria-label={t('admin.sus.form.loading')} />}>
        <SusMapPicker
          value={value}
          onChange={handleChange}
          disabled={disabled}
          ariaLabel={t('admin.sus.form.position.map')}
        />
      </Suspense>
      <Group justify="space-between" gap="xs">
        <Text size="sm" aria-live="polite">
          {value
            ? t('admin.sus.detail.coordinates.value', {
                latitude: value.latitude,
                longitude: value.longitude,
              })
            : t('admin.sus.form.position.empty')}
        </Text>
        <Button
          variant="subtle"
          size="compact-sm"
          onClick={() => handleChange(null)}
          disabled={disabled || !value}
        >
          {t('admin.sus.form.position.clear')}
        </Button>
      </Group>
      {error && (
        <Text size="sm" c="red" role="alert">
          {t('admin.sus.form.error.position')}
        </Text>
      )}
    </Stack>
  );
};

export default SusPositionField;
